import { FC, useId, useMemo, useState } from 'react';
import useResponsive from 'src/hooks/contexts/useResponsive';
import {
	StyledFullContainer,
	StyledHoverGlow
} from 'src/styles/theme/components/NGGoldContainer/NGGoldContainer.styles';
import NGIcon from 'src/styles/theme/components/NGIcon';
import NGSkeleton from 'src/styles/theme/components/NGSkeleton';
import NGText from 'src/styles/theme/components/NGText';
import { EFontFamily, ETextType } from 'src/styles/theme/components/NGText/NGText.enum';
import { ESize, ETextColor, EThemeColor } from 'src/styles/theme/utils/enums';
import { NGSpinnerEx, NGTextEx, StyledButton } from './NGButton.styles';
import { IButtonData, INGButton } from './NGButton.type';

const spinnerPosition = {
	right: '5px'
};

const NGButton: FC<INGButton> = ({
	children,
	onClick,
	disabled = false,
	isHidden = false,
	size = ESize.m,
	ariaLabel,
	counterValue,
	firstLetterFactor = 1.2,
	isLink = false,
	secondary = false,
	isActive = false,
	fullWidth = false,
	isWaiting = false,
	colorVariant = EThemeColor.gold,
	iconSize = ESize.s,
	startIcon,
	endIcon,
	dataCy,
	iconColor,
	role,
	...otherProps
}) => {
	const { isSmallerThanMd } = useResponsive();
	const glowContainerUuid = useId();

	const [isGlowVisible, setIsGlowVisible] = useState(false);
	const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

	const buttonData: IButtonData = isLink
		? {
				as: 'a',
				type: undefined
		  }
		: {
				as: 'button',
				type: 'button'
		  };

	const responsiveSize = useMemo(() => {
		if (!isSmallerThanMd) {
			return size;
		}

		// get previous item in enum
		const enumValues = Object.values(ESize);
		const enumIndex = enumValues.indexOf(size);

		if (enumIndex === 0) {
			return size;
		}

		return enumValues[enumIndex - 1];
	}, [isSmallerThanMd, size]);

	const onMouseEnter = () => {
		try {
			// @ts-ignore
			clearTimeout(window[`timer-${glowContainerUuid}`]);
		} catch {
			// fallback
		}

		setIsGlowVisible(true);
	};

	const onMouseLeave = () => {
		try {
			// @ts-ignore
			window[`timer-${glowContainerUuid}`] = setTimeout(() => {
				setIsGlowVisible(false);
			}, 100);
		} catch (err) {
			console.error(err);
		}
	};

	const onMouseMove = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		const parent = document.getElementById(glowContainerUuid);

		const { left, top } = parent?.getBoundingClientRect() || {};

		if (!left || !top) {
			return;
		}

		setMousePos({ x: e.clientX - left, y: e.clientY - top });
	};

	const position = {
		left: `${mousePos.x}px`,
		top: `${mousePos.y}px`
	};

	if (otherProps.isLoading && !isHidden) {
		return <NGSkeleton width={otherProps.skWidth || '100%'} height={otherProps.skHeight || size} />;
	}

	return (
		<StyledButton
			as={buttonData.as}
			type={buttonData.type}
			onClick={isWaiting || isLink || isHidden ? undefined : onClick}
			data-cy={dataCy}
			disabled={disabled}
			isHidden={isHidden}
			secondary={secondary}
			fullWidth={fullWidth}
			size={responsiveSize}
			aria-label={ariaLabel || (typeof children === 'string' ? children : '')}
			colorVariant={colorVariant}
			role={role}
			onMouseMove={onMouseMove}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
			{...otherProps}
		>
			{!disabled ? (
				<StyledFullContainer as='span' id={glowContainerUuid}>
					<StyledHoverGlow isVisible={isGlowVisible} style={position} colorVariant={colorVariant} />
				</StyledFullContainer>
			) : null}

			{startIcon ? <NGIcon color={iconColor} type={startIcon} size={iconSize} paddingRight={ESize['4xs']} /> : null}

			{typeof children === 'string' ? (
				<>
					<NGText
						type={ETextType.span}
						size={responsiveSize}
						fontFamily={EFontFamily.cinzel}
						textColor={secondary ? (isActive ? ETextColor.white : ETextColor.grey) : undefined}
						shadowColor={secondary ? (isActive ? EThemeColor.white : undefined) : undefined}
						inheritStyle={false}
						firstLetterFactor={!!firstLetterFactor ? firstLetterFactor : undefined}
						singleLine={!secondary}
					>
						{children}
					</NGText>

					{!!counterValue ? (
						<NGTextEx type={ETextType.span} textColor={ETextColor.black} isActivated={counterValue > 0}>
							{counterValue}
						</NGTextEx>
					) : null}
					{endIcon ? <NGIcon color={iconColor} type={endIcon} size={iconSize} paddingLeft={ESize['4xs']} /> : null}
					{isWaiting ? <NGSpinnerEx size={ESize.s} isActivated={isWaiting} style={spinnerPosition} /> : null}
				</>
			) : (
				children
			)}
		</StyledButton>
	);
};

export { NGButton };
